import { z } from "zod/v4"
import { UserId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"

export const UserSecretDocumentMeta = z.object({
  virtual_cast: z.union([
    z.object({
      kind: z.literal("started"),
      state: z.string().describe("OAuth の認可コード取得時の state の値です。"),
      code_verifier: z.string().describe("OAuth の code challenge 生成に用いた code verifier です。"),
      redirect_uri: z.string().describe("OAuth の auth code 取得時に利用した redirect_uri の値です。"),
    }),
    z.object({
      kind: z.literal("authed"),
      access_token: z.string().describe("OAuth のアクセストークンです。"),
      refresh_token: z.string().describe("OAuth のリフレッシュトークンです。"),
      expires_in: z.number().describe("アクセストークンの有効期限が切れるまでの秒数です。"),
      created_at: z.number().describe("このオブジェクトの作成日時 (UNIX Timestamp mills) です。"),
      updated_at: z.number().describe("このオブジェクトの更新日時 (UNIX Timestamp mills) です。"),
      user_id: z.number().optional().describe("Virtualcast のユーザーID。連携確立時に VC API /user から取得して保存する。"),
    })
  ]).nullable().describe("VirtualCastのOAuth連携情報。クライアントからは直接読み取れず、サーバー内でのみ読み取れます。"),
})

export type UserSecretDocumentMeta = z.infer<typeof UserSecretDocumentMeta>

export const UserSecretDocumentBody = z.intersection(
  UserSecretDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type UserSecretDocumentBody = z.infer<typeof UserSecretDocumentBody>

export type UserSecretDocument = UserSecretDocumentBody & {
  id: UserId,
}

export class UserSecretDocuments {

  static toDocumentData(doc: UserSecretDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserSecretDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: UserId(doc.id),
      virtual_cast: data["virtual_cast"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const GenerateVirtualCastOAuthAuthURLRequest = z.object({
  redirectUri: z.union([
    z.literal("http://localhost:3000/oauth/virtualcast/callback"),
    z.literal("http://localhost:3001/oauth/virtualcast/callback"),
    z.literal("http://localhost:3003/oauth/virtualcast/callback"),
    z.literal("https://popopo-alpha-web.web.app/oauth/virtualcast/callback"),
    z.literal("https://popopo-beta-web.web.app/oauth/virtualcast/callback"),
    z.literal("https://popopo-dev-web.web.app/oauth/virtualcast/callback"),
    z.literal("https://popopo-stage-web.web.app/oauth/virtualcast/callback"),
    z.literal("popopoalpha://oauth/vc"),
    z.literal("popopodev://oauth/vc"),
    z.literal("popopostage://oauth/vc"),
    z.literal("https://www.popopo.com/oauth/virtualcast/callback"),
  ])
})

export type GenerateVirtualCastOAuthAuthURLRequest = z.infer<typeof GenerateVirtualCastOAuthAuthURLRequest>

export const GenerateVirtualCastOAuthAccessTokenRequest = z.object({
  code: z.string(),
  state: z.string(),
})

export type GenerateVirtualCastOAuthAccessTokenRequest = z.infer<typeof GenerateVirtualCastOAuthAccessTokenRequest>

export const VirtualCastOAuthRelayAccessRequest = z.object({
  method: z.union([
    z.literal("get"),
    z.literal("post"),
    z.literal("put"),
    z.literal("delete"),
  ]),
  path: z.union([
    z.literal("/user"),
    z.literal("/user/items")
  ]),
  body: z.object({
    query: z.string(),
    body: z.string(),
  })
})

export type VirtualCastOAuthRelayAccessRequest = z.infer<typeof VirtualCastOAuthRelayAccessRequest>
